import { useEffect, useState } from 'react';
import type { ListaMisMensajes, MiMensaje, MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { haySiguiente, usePaginacionCursor } from '../logica/paginacion';
import { REENVIOS_AL_DIA, textoReenvios } from './logica';
import { BotonPc, Pagina } from './Pagina';

/**
 * «Mis mensajes» (5.7, `/mis-mensajes`): con el token de cliente de «Mis datos» (2.9), el negocio a elegir entre sus
 * perfiles (`listMyBusinessProfiles`, G35) y lo que le ha mandado ahí (`listMyDeliveries`): título, de qué es
 * (`deliverable_label`, G302), cuándo y por dónde, el estado y «Reenviar» con los tres puntos del día
 * (`resends_remaining_today`, G307); si no se puede, el porqué (`resend_unavailable_reason`). Pedirlo otra vez va sin
 * ventana (barato y con límite): 429 si se pasa, 409 si ya no se puede. Nunca los avisos internos del equipo (G14).
 * Pie con las dos privacidades (G195).
 */

const ESTADO: Readonly<Record<MiMensaje['status'], readonly [string, EstadoChip]>> = {
    pending: ['Enviando', 'pending'],
    queued: ['Enviando', 'pending'],
    sent: ['Enviado', 'pending'],
    delivered: ['Entregado', 'live'],
    read: ['Leído', 'live'],
    fallback: ['Entregado', 'live'],
    failed: ['No ha llegado', 'problem'],
    discarded: ['No ha llegado', 'problem'],
};

const CANAL: Readonly<Record<string, readonly [string, NombreIcono]>> = {
    whatsapp: ['WhatsApp', 'message-circle'],
    instagram: ['Instagram', 'instagram'],
    telegram: ['Telegram', 'send'],
    email: ['email', 'mail'],
    sms: ['SMS', 'smartphone'],
    voice: ['teléfono', 'phone'],
};

const cuando = (iso: string) => {
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const hoy = new Date();
    const hora = f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const ayer = new Date(hoy.getTime() - 86_400_000);
    if (f.toDateString() === hoy.toDateString()) {
        return `Hoy, ${hora}`;
    }
    if (f.toDateString() === ayer.toDateString()) {
        return `Ayer, ${hora}`;
    }
    return `${f.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}, ${hora}`;
};

interface Props {
    ops: OperacionesClienteFinal;
    onSalir: () => void;
    onCaducada: () => void;
    /** «Cambiar» el canal: lleva a «Mis datos» (2.10). */
    onCambiar: () => void;
}

export function MisMensajes({ ops, onSalir, onCaducada, onCambiar }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [error, setError] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [lista, setLista] = useState<{ clave: string; datos: ListaMisMensajes | null; error: boolean } | null>(null);
    const [intento, setIntento] = useState(0);
    const [aviso, setAviso] = useState<{ id: string; texto: string; error: boolean } | null>(null);
    const [ocupado, setOcupado] = useState<string | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (vivo) {
                    setPerfiles(l.data.filter((p) => p.ended == null));
                    setError(false);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setError(true);
                }
            });
        return () => {
            vivo = false;
        };
        // onCaducada cambia en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        ops.listMyDeliveries(idPerfil, { cursor: pag.cursor, limit: 20 })
            .then((datos) => {
                if (vivo) {
                    setLista({ clave, datos, error: false });
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setLista({ clave, datos: null, error: true });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, pag.cursor, clave]);

    const reenviar = (m: MiMensaje) => {
        if (idPerfil === null) {
            return;
        }
        setOcupado(m.id);
        setAviso(null);
        ops.resendMyDelivery(idPerfil, m.id)
            .then(() => {
                setAviso({ id: m.id, texto: 'Te lo hemos mandado otra vez. Llegará en unos minutos.', error: false });
                setIntento((i) => i + 1);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                    return;
                }
                const texto =
                    e instanceof ErrorApi && e.estado === 429
                        ? 'Ya lo has pedido 3 veces hoy. Mañana podrás otra vez.'
                        : e instanceof ErrorApi && e.estado === 409
                          ? (e.problema.detail ?? 'Este mensaje ya no se puede pedir otra vez.')
                          : 'No hemos podido mandarlo. Prueba de nuevo.';
                setAviso({ id: m.id, texto, error: true });
                setIntento((i) => i + 1);
            })
            .finally(() => {
                setOcupado(null);
            });
    };

    const nombre = perfil?.business.name ?? '';
    const cabecera = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Mis mensajes' } as const);
    const pie = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, privacidad: perfil.business.privacy_url ?? null } as const);
    const reintentar = () => { setIntento((i) => i + 1); };

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus mensajes</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setError(false); reintentar(); }} />
                </section>
            </Pagina>
        );
    }

    const actual = lista?.clave === clave ? lista : null;
    const mensajes = actual?.datos?.data ?? null;
    const canal = perfil?.preferred_channel ?? null;
    const [nombreCanal, iconoCanal] = canal === null ? ['', 'message-circle' as NombreIcono] : (CANAL[canal] ?? [canal, 'send']);

    return (
        <Pagina
            cabecera={cabecera}
            pie={pie}
            onSalir={onSalir}
            titulo={{ titulo: 'Mis mensajes', sub: nombre === '' ? 'Lo que te ha mandado el negocio. Si algo no te ha llegado o lo has borrado, pídelo otra vez.' : `Lo que te ha mandado ${nombre}. Si algo no te ha llegado o lo has borrado, pídelo otra vez.` }}
        >
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select
                        className="rv-pc-select rv-pc-select--ancho"
                        value={perfil?.customer_id ?? ''}
                        onChange={(e) => {
                            setElegido(e.target.value);
                            setAviso(null);
                            reiniciar();
                        }}
                    >
                        {perfiles.map((p) => (
                            <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>
                        ))}
                    </select>
                </label>
            )}
            {canal !== null && (
                <section className="rv-pc-card rv-pc-card--seccion" aria-label="Por dónde te los mandamos">
                    <div className="rv-pc-pago__cab" style={{ cursor: 'default' }}>
                        <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre={iconoCanal} /></span>
                        <span>
                            <small>Te los mandamos por</small>
                            <strong>{canal === 'email' ? 'Email' : nombreCanal}</strong>
                        </span>
                        <BotonPc texto="Cambiar" icono="pencil" tipo="sec" peq onClick={onCambiar} />
                    </div>
                    {canal !== 'email' && <p className="rv-pc-nota"><span>Si no llega por ahí, te lo mandamos por email.</span></p>}
                </section>
            )}
            <section className="rv-pc-card rv-pc-card--seccion" aria-label="Recibidos" aria-busy={perfiles === null || (perfil !== null && actual === null)}>
                <div className="rv-pc-card__cab">
                    <h2>Recibidos</h2>
                    <span>Puedes pedir otra vez una confirmación o un pase hasta 3 veces al día.</span>
                </div>
                {perfiles === null || (perfil !== null && actual === null) ? (
                    <>
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 56, marginTop: 12 }} />
                    </>
                ) : actual?.error === true ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="circle-alert" /></span>
                        <strong>No hemos podido cargar tus mensajes</strong>
                        <BotonPc texto="Reintentar" icono="refresh-cw" tipo="sec" peq onClick={reintentar} />
                    </div>
                ) : mensajes === null || mensajes.length === 0 ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="message-square-text" /></span>
                        <strong>Todavía no te hemos mandado nada aquí</strong>
                        <small>Cuando reserves, compres entradas o te apuntes a una lista, verás aquí las confirmaciones y los pases.</small>
                    </div>
                ) : (
                    mensajes.map((m) => <FilaMensaje key={m.id} m={m} ocupado={ocupado === m.id} aviso={aviso?.id === m.id ? aviso : null} onReenviar={() => { reenviar(m); }} />)
                )}
            </section>
            {actual?.datos != null && (pag.hayAnterior || haySiguiente(actual.datos.next_cursor)) && (
                <div className="rv-pc-paginas">
                    <BotonPc texto="Anterior" icono="arrow-left" tipo="sec" peq desactivado={!pag.hayAnterior} onClick={pag.anterior} />
                    <BotonPc texto="Siguiente" icono="arrow-right" tipo="sec" peq desactivado={!haySiguiente(actual.datos.next_cursor)} onClick={() => { pag.siguiente(actual.datos?.next_cursor ?? null); }} />
                </div>
            )}
            <p className="rv-pc-nota rv-final-osc"><Icono nombre="clock" /><span>Guardamos lo que te mandamos hasta 30 días después de la fecha. Después ya no se puede pedir otra vez.</span></p>
        </Pagina>
    );
}

function FilaMensaje({ m, ocupado, aviso, onReenviar }: { m: MiMensaje; ocupado: boolean; aviso: { texto: string; error: boolean } | null; onReenviar: () => void }) {
    const [estado, chip] = ESTADO[m.status];
    const quedan = Math.max(0, Math.min(REENVIOS_AL_DIA, m.resends_remaining_today ?? 0));
    const usados = REENVIOS_AL_DIA - quedan;
    const canal = m.final_channel == null ? null : (CANAL[m.final_channel]?.[0] ?? m.final_channel);
    // «No llegó por WhatsApp» con el canal que se intentó primero (G514); sin él, el texto genérico.
    const preferido = m.preferred_channel == null ? null : (CANAL[m.preferred_channel]?.[0] ?? m.preferred_channel);
    const respaldo = m.used_fallback === true ? (preferido === null ? 'No llegó por tu canal preferido' : `No llegó por ${preferido}`) : null;
    const linea = [cuando(m.created_at), canal === null ? null : `por ${canal}`, respaldo].filter((x): x is string => x !== null && x !== '').join(' · ');
    return (
        <div className="rv-pc-pago">
            <div className="rv-pc-pago__cab" style={{ cursor: 'default' }}>
                <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre={m.final_channel === 'email' ? 'mail' : 'message-circle'} /></span>
                <span>
                    <strong>{m.title ?? 'Mensaje'}</strong>
                    {m.deliverable_label != null && <small>{m.deliverable_label}</small>}
                    <small>{linea}</small>
                </span>
                <span className="rv-pc-pago__importe"><Chip estado={chip}>{estado}</Chip></span>
            </div>
            <div className="rv-pc-pago__cuerpo">
                {m.resendable ? (
                    <div className="rv-pc-dato" style={{ borderTop: 'none', alignItems: 'center' }}>
                        <span>
                            <span aria-hidden="true">{'●'.repeat(usados) + '○'.repeat(quedan)} </span>
                            {textoReenvios(quedan)}
                        </span>
                        <BotonPc texto="Reenviar" icono="send" tipo="sec" peq ocupado={ocupado} ocupadoTexto="Enviando…" desactivado={quedan === 0} onClick={onReenviar} />
                    </div>
                ) : (
                    m.resend_unavailable_reason != null && <p className="rv-pc-nota"><Icono nombre="info" /><span>{m.resend_unavailable_reason}</span></p>
                )}
                {aviso !== null && (
                    <p className="rv-pc-nota" role={aviso.error ? 'alert' : 'status'} style={aviso.error ? { color: 'var(--rv-red)' } : undefined}>
                        <Icono nombre={aviso.error ? 'circle-alert' : 'circle-check'} />
                        <span>{aviso.texto}</span>
                    </p>
                )}
            </div>
        </div>
    );
}
